/**
 * Vault — Censo de cuentas compartidas (F6, solo admin). Mockup vault-05.
 * Las 49 cuentas del Anexo A con dueño, uso declarado y destino (árbol A/B/C/D).
 * El censo se completa con un formulario que le llega al dueño propuesto por DM.
 */
import { useMemo, useState } from 'react';
import { CheckSquare, Download, ListChecks, Lock, Plus, Search, Upload, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState, LoaderBlock, StatCard, fmtNum } from '@/components/internal/shared';
import { AreaBadge, useVaultLabels } from '@/components/vault/vault-ui';
import { SharedAccountDialog } from '@/components/vault/SharedAccountDialog';
import { errMsg } from '@/hooks/useVault';
import { downloadCsv } from '@/lib/csv';
import { todayAR } from '@/../../shared/dates';
import {
  CENSUS_DESTINOS,
  CENSUS_ESTADOS,
  type CensusDestino,
  type CensusEstado,
  type VaultSharedAccount,
} from '../../../../shared/types/vault';

const ALL = '__all__';

const DESTINO_VARIANT: Record<CensusDestino, 'default' | 'secondary' | 'outline' | 'success'> = {
  grupo: 'success',
  individual: 'default',
  service: 'secondary',
  vault: 'outline',
  baja: 'secondary',
  relevar: 'outline',
};
const ESTADO_VARIANT: Record<CensusEstado, 'default' | 'secondary' | 'outline' | 'success' | 'destructive'> = {
  sin_relevar: 'destructive',
  en_analisis: 'secondary',
  relevada: 'success',
};

export function SharedAccountsPage() {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const utils = trpc.useUtils();
  const list = trpc.vaultAdmin.listSharedAccounts.useQuery(undefined, { staleTime: 30_000 });
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState<string>(ALL);
  const [destino, setDestino] = useState<string>(ALL);
  const [editing, setEditing] = useState<VaultSharedAccount | null | undefined>(undefined); // undefined = cerrado, null = nueva
  const [confirmSeed, setConfirmSeed] = useState(false);

  const seed = trpc.vaultAdmin.seedCensus.useMutation({
    onSuccess: (r) => {
      utils.vaultAdmin.listSharedAccounts.invalidate();
      toast.success(t('vault.census.seeded', 'Anexo A cargado: {{n}} cuentas nuevas, {{s}} ya existían', { n: r.inserted, s: r.skipped }));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
  const stats = list.data?.stats;
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (estado !== ALL && r.estado !== estado) return false;
      if (destino !== ALL && r.destino !== destino) return false;
      if (!needle) return true;
      return [r.accountEmail, r.ownerName, r.ownerEmail, r.probableUse, r.declaredUse, r.area].some((v) => (v ?? '').toLowerCase().includes(needle));
    });
  }, [rows, q, estado, destino]);
  const { pageItems, footer: paginationFooter } = usePagination(filtered, 25);

  const exportCsv = () => {
    downloadCsv(
      `censo-cuentas-compartidas-${todayAR()}.csv`,
      ['cuenta', 'dueño', 'email dueño', 'área', 'uso probable', 'uso declarado', 'destino', 'estado', 'MFA', 'dónde vive el 2FA', 'plataformas', 'formulario enviado', 'respondido', 'notas'],
      rows.map((r) => [
        r.accountEmail,
        r.ownerName ?? '',
        r.ownerEmail ?? '',
        r.area ?? '',
        r.probableUse ?? '',
        r.declaredUse ?? '',
        L.destino(r.destino),
        L.estado(r.estado),
        r.hasMfa == null ? '' : r.hasMfa ? 'sí' : 'no',
        r.mfaLocation ?? '',
        (r.platforms ?? []).join('; '),
        r.formSentAt ?? '',
        r.answeredAt ?? '',
        r.notes ?? '',
      ]),
    );
  };

  if (list.isLoading) return <LoaderBlock />;
  // Un error nunca se disfraza de "censo vacío" (política de roles y visibilidad).
  if (list.isError) {
    return (
      <div className="mx-auto max-w-7xl p-2 sm:p-4">
        <EmptyState title={t('vault.census.load_error', 'No se pudo cargar el censo')} description={errMsg(list.error)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-2 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
            <ListChecks className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl">{t('vault.census.title', 'Censo de cuentas compartidas')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('vault.census.subtitle', '{{n}} cuentas identificadas. Cada una necesita dueño, uso declarado y destino: grupo, accesos individuales, service account o vault.', { n: rows.length })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            {t('vault.census.export', 'Exportar')}
          </Button>
          {rows.length === 0 && (
            <Button variant="outline" onClick={() => setConfirmSeed(true)} disabled={seed.isPending}>
              <Upload className="mr-2 h-4 w-4" />
              {t('vault.census.seed', 'Cargar Anexo A')}
            </Button>
          )}
          <Button onClick={() => setEditing(null)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('vault.census.new', 'Nueva cuenta')}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={CheckSquare} label={t('vault.census.tile_done', 'Relevadas')} value={`${fmtNum(stats?.byEstado.relevada ?? 0)} / ${fmtNum(stats?.total ?? 0)}`} tone="blue" />
        <StatCard icon={ListChecks} label={t('vault.census.tile_group', '→ Grupo / alias')} value={fmtNum(stats?.byDestino.grupo ?? 0)} tone="green" />
        <StatCard icon={UserRound} label={t('vault.census.tile_individual', '→ Accesos individuales')} value={fmtNum(stats?.byDestino.individual ?? 0)} tone="purple" />
        <StatCard icon={Lock} label={t('vault.census.tile_vault', '→ Irreducibles (Vault)')} value={fmtNum(stats?.byDestino.vault ?? 0)} tone="amber" />
      </div>

      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('vault.census.search', 'Buscar cuenta…')} className="pl-8" />
          </div>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('vault.census.f_estado_all', 'Estado: todos')}</SelectItem>
              {CENSUS_ESTADOS.map((e) => <SelectItem key={e} value={e}>{L.estado(e)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={destino} onValueChange={setDestino}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('vault.census.f_destino_all', 'Destino: todos')}</SelectItem>
              {CENSUS_DESTINOS.map((d) => <SelectItem key={d} value={d}>{L.destino(d)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title={rows.length === 0 ? t('vault.census.empty', 'El censo está vacío') : t('vault.census.empty_filtered', 'Nada coincide con el filtro')}
            description={rows.length === 0 ? t('vault.census.empty_body', 'Cargá las 49 cuentas del Anexo A del doc o creá una a mano.') : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('vault.census.col_account', 'Cuenta')}</TableHead>
                  <TableHead>{t('vault.census.col_owner', 'Dueño')}</TableHead>
                  <TableHead>{t('vault.census.col_area', 'Área')}</TableHead>
                  <TableHead>{t('vault.census.col_use', 'Para qué se usa')}</TableHead>
                  <TableHead>{t('vault.census.col_destino', 'Destino propuesto')}</TableHead>
                  <TableHead>{t('vault.census.col_form', 'Formulario')}</TableHead>
                  <TableHead className="text-right">{t('vault.census.col_estado', 'Estado')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((r) => (
                  <TableRow key={r.accountEmail} className="cursor-pointer" onClick={() => setEditing(r)}>
                    <TableCell className="font-mono text-xs">{r.accountEmail}</TableCell>
                    <TableCell>{r.ownerName ?? r.ownerEmail ?? <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><AreaBadge area={r.area} /></TableCell>
                    <TableCell className="max-w-[360px] truncate text-sm" title={r.declaredUse ?? r.probableUse}>
                      {r.declaredUse ?? r.probableUse ?? <span className="text-muted-foreground">{t('vault.census.undeclared', 'Sin declarar')}</span>}
                    </TableCell>
                    <TableCell><Badge variant={DESTINO_VARIANT[r.destino]}>{L.destino(r.destino)}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.answeredAt
                        ? t('vault.census.form_answered', 'Respondido')
                        : r.formSentAt
                          ? t('vault.census.form_sent', 'Enviado')
                          : r.formError
                            ? <span className="text-rose-600" title={r.formError}>{t('vault.census.form_failed', 'Envío falló')}</span>
                            : '—'}
                    </TableCell>
                    <TableCell className="text-right"><Badge variant={ESTADO_VARIANT[r.estado]}>{L.estado(r.estado)}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {paginationFooter}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {t('vault.census.footer', 'Fuente: sheet de cuentas compartidas de IT · el censo se completa con un formulario que le llega al dueño propuesto.')}
        </p>
      </Card>

      {editing !== undefined && (
        <SharedAccountDialog open onOpenChange={(o) => { if (!o) setEditing(undefined); }} account={editing} />
      )}

      <AlertDialog open={confirmSeed} onOpenChange={setConfirmSeed}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.census.seed_title', '¿Cargar las 49 cuentas del Anexo A?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('vault.census.seed_body', 'Carga el listado del doc con el uso probable y el destino preliminar. Es idempotente: nunca pisa una cuenta que ya existe.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => seed.mutate()}>{t('vault.census.seed', 'Cargar Anexo A')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
